import { useTheme } from '@material-ui/core/styles'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { forwardRef } from 'react'

import DialerController from '@src/app/command/DialerCommand/store/DialerController'
import ForwardPhoneNumberSelectorController from '@src/app/command/forward-phone-number-picker/ForwardPhoneNumberSelectorController'
import { AppAvatar } from '@src/app/components/app-avatar'
import { useAppStore } from '@src/app/context'
import { useFlag } from '@src/app/feature-flags'
import AddParticipantController from '@src/app/voice/store/AddParticipantController'
import CallTransferController from '@src/app/voice/store/CallTransferController'
import { MenuItem } from '@src/component/menu-v2'
import { highlight } from '@src/lib'
import { formatted } from '@src/lib/phone-number'
import Tooltip from '@ui/Tooltip'
import Typography from '@ui/Typography'
import { ListenerIcon } from '@ui/icons/tint/14/general'
import { BusinessHoursIcon } from '@ui/icons/tint/16/general'
import { GeneralNumberHashIcon } from '@ui/icons/tint/20/general'
import { HStack } from '@ui/layout/Stack'
import palette from '@ui/theme/themes/dark/palette'

import { AddNumberRow, MemberRow } from './PhoneNumberSelectorController'
import type { IdentityRow } from './PhoneNumberSelectorController'
import type PhoneNumberSelectorController from './PhoneNumberSelectorController'
import * as styles from './row-item.css'

interface RowItemProps {
  controller: PhoneNumberSelectorController
  row: IdentityRow
}

/**
 * @deprecated
 */
const RowItem = forwardRef<HTMLButtonElement | HTMLAnchorElement, RowItemProps>(
  ({ controller, row }, outerRef) => {
    const theme = useTheme()
    const { service } = useAppStore()
    const addMultipleParticipantsFlagEnabled = useFlag(
      'groupCallingAddMultipleParticipants',
    )
    const {
      identity,
      selection,
      phones,
      rowMeta,
      phoneNumberMeta,
      isOffHours,
      showPhoneNumbers,
      handleSelect,
    } = row
    const { disabled, tooltipDisabledReason, labelDisabledReason } =
      rowMeta.get(row.id) ??
      // @ts-expect-error unchecked index access
      (phones.length === 1 ? phoneNumberMeta.get(phones[0]?.number) : {}) ??
      {}

    const highlighted = controller.keyboardList.stepper.item === row

    const onListenerClick = () => {
      if (controller instanceof AddParticipantController && row instanceof MemberRow) {
        controller.listeners.push(row)
      }
    }

    const { tooltipTitle, tooltipBody } = (() => {
      if (tooltipDisabledReason) {
        return {
          tooltipTitle: tooltipDisabledReason,
          tooltipBody: undefined,
        }
      }

      if (!addMultipleParticipantsFlagEnabled) {
        return {
          tooltipTitle: '',
          tooltipBody: undefined,
        }
      }

      if (controller.size === 30 && controller.activeRow !== row) {
        return {
          tooltipTitle: 'Add to list ⏎',
          tooltipBody: 'Add and start call  ⌘ ⏎',
        }
      }

      return {
        tooltipTitle: '',
        tooltipBody: undefined,
      }
    })()

    const addDisabledTooltip = (content: ReactElement) => (
      <Tooltip
        title={tooltipTitle}
        body={tooltipBody}
        themeKey="dark"
        delay={controller.size === 24 ? 0 : 1000}
        placement={controller.size === 24 || tooltipDisabledReason ? 'top' : 'top right'}
        disabled={
          row !== controller.keyboardList.stepper.item ||
          (controller instanceof ForwardPhoneNumberSelectorController &&
            !tooltipDisabledReason) ||
          ((!disabled || !tooltipDisabledReason) && controller.size === 24)
        }
        className={cx({
          [styles.tooltipComponent]: !(
            controller.size === 30 && controller.activeRow !== row
          ),
        })}
      >
        {content}
      </Tooltip>
    )

    return row instanceof AddNumberRow ? (
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      <MenuItem
        disabled={disabled}
        className={cx(
          styles.root,
          styles.item({ size: controller.size, selected: !!selection, disabled }),
        )}
        icon={
          <GeneralNumberHashIcon
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            color={selection ? theme.palette.op.white : theme.palette.primary.main}
          />
        }
        applyHoverStyles={false}
        onClick={handleSelect}
      >
        {addDisabledTooltip(
          <span>
            <HStack component="span">
              <Typography
                variant="footnote"
                className={cx({ [styles.highlight]: !row.selection })}
                dangerouslySetInnerHTML={{
                  __html: `${controller.lang.SELECT_CUSTOM} ${highlight(
                    row.formattedNumber || '',
                    controller.search,
                  )}`,
                }}
              />
              <Typography
                nowrap
                variant={controller.size === 24 ? 'caption2' : 'body'}
                color={
                  disabled || (isOffHours && !selection)
                    ? 'textTertiary'
                    : selection
                    ? `rgb(${palette.gray[1]})`
                    : 'textSecondary'
                }
                className={cx(
                  { [styles.highlight]: !row.selection },
                  styles.number({ size: controller.size }),
                )}
                dangerouslySetInnerHTML={{
                  __html:
                    disabled && labelDisabledReason
                      ? labelDisabledReason
                      : phones.length > 1
                      ? `${phones.length} ${
                          Number(controller.search) ? 'matches' : 'numbers'
                        }`
                      : // @ts-expect-error unchecked index access

                        highlight(formatted(phones[0]?.number) || '', controller.search),
                }}
              />
            </HStack>
          </span>,
        )}
      </MenuItem>
    ) : (
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      <MenuItem
        ref={outerRef}
        disabled={disabled}
        className={cx(
          styles.root,
          styles.item({ size: controller.size, selected: !!selection, disabled }),
        )}
        height="small"
        applyHoverStyles={false}
        icon={
          identity ? (
            <AppAvatar
              identity={identity}
              size={20}
              className={disabled ? styles.disabledAvatar : undefined}
            />
          ) : null
        }
        onClick={disabled ? undefined : handleSelect}
      >
        {addDisabledTooltip(
          <div className={styles.wrapper}>
            <Typography
              nowrap
              variant={controller.size === 24 ? 'footnote' : 'body'}
              color={
                disabled || (isOffHours && !selection)
                  ? 'textTertiary'
                  : selection
                  ? `rgb(${palette.gray[1]})`
                  : 'textPrimary'
              }
              className={cx({ [styles.highlight]: !row.selection })}
              dangerouslySetInnerHTML={{
                __html: highlight(
                  `${identity?.name ?? ''}${
                    row instanceof MemberRow && row.member.statusSymbol
                      ? ` ${row.member.statusSymbol}`
                      : ''
                  }`.trim(),
                  controller.search,
                ),
              }}
            />
            {isOffHours && (
              <BusinessHoursIcon
                className={cx(styles.icon, { [styles.businessHours]: !selection })}
              />
            )}
            {showPhoneNumbers ||
            (disabled && labelDisabledReason) ||
            // FIXME: this is hack due to the current nerf of direct dialing
            ((controller instanceof CallTransferController ||
              controller instanceof DialerController ||
              controller instanceof AddParticipantController) &&
              row instanceof MemberRow) ? (
              <Typography
                nowrap
                variant={controller.size === 24 ? 'footnote' : 'body'}
                color={
                  disabled || (isOffHours && !selection)
                    ? 'textTertiary'
                    : selection
                    ? `rgb(${palette.gray[1]})`
                    : 'textSecondary'
                }
                className={cx(
                  { [styles.highlight]: !row.selection },
                  styles.number({ size: controller.size }),
                )}
                dangerouslySetInnerHTML={{
                  __html:
                    disabled && labelDisabledReason
                      ? labelDisabledReason
                      : phones.length > 1
                      ? `${phones.length} ${
                          Number(controller.search) ? 'matches' : 'numbers'
                        }`
                      : // @ts-expect-error unchecked index access
                        highlight(formatted(phones[0]?.number) || '', controller.search),
                }}
              />
            ) : null}
            {service.flags.flags.groupCallingListener &&
            controller instanceof AddParticipantController &&
            row instanceof MemberRow &&
            highlighted ? (
              <Tooltip title="Add as a listener" themeKey="dark">
                <div
                  className={styles.listener}
                  onClick={disabled ? undefined : onListenerClick}
                >
                  <ListenerIcon className={styles.listenerIcon} />
                </div>
              </Tooltip>
            ) : null}
          </div>,
        )}
      </MenuItem>
    )
  },
)

// eslint-disable-next-line @typescript-eslint/no-deprecated
export default observer(RowItem)
